import React, { FC, SVGProps, useEffect, useMemo, useState } from 'react';
import { ProductRecognitionBBox, ProductRecognitionPolygon, Rect } from '../types';
import { areBoxOverlapping } from '../utils/geometry';
import { getContainedSVGCoordinates } from '../utils/getContainedSVGCoordinates';

const MASK_OPACITY = 0.8;

/**
 * Parse a hex color string (#rgb, #rrggbb, or #rrggbbaa) into [r, g, b].
 */
const parseHexColor = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  if (h.length === 3) {
    return [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16)];
  }
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

/**
 * Process a 1-bit PNG mask: upscale to target dimensions using nearest-neighbor
 * make black pixels transparent,
 * colorize light pixels with the given color, and apply semi-transparency.
 */
const processMaskImage = (
  b64: string,
  targetWidth: number,
  targetHeight: number,
  color?: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Upscale the small mask to full image dimensions using nearest-neighbor
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas context not available'));

      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const [cr, cg, cb] = color ? parseHexColor(color) : [255, 255, 255];

      for (let i = 0; i < data.length; i += 4) {
        const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        if (brightness < 128) {
          data[i + 3] = 0;
        } else {
          data[i] = cr;
          data[i + 1] = cg;
          data[i + 2] = cb;
          data[i + 3] = Math.round(data[i + 3] * MASK_OPACITY);
        }
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = `data:image/png;base64,${b64}`;
  });
};

const bboxPadding =
  (padding = 10, maxSize: [number, number] | null = null) =>
  (b: Rect): Rect => {
    if (!!b && b.length === 4) {
      let [x, y, dx, dy] = b;

      if (dx < 0) {
        x = x + dx;
        dx = Math.abs(dx);
      }

      if (dy < 0) {
        y = y + dy;
        dy = Math.abs(dy);
      }

      const box: Rect = [x - padding, y - padding, dx + padding * 2, dy + padding * 2];

      if (maxSize) {
        return [
          Math.max(0, box[0]),
          Math.max(0, box[1]),
          Math.min(maxSize[0], box[2]),
          Math.min(maxSize[1], box[3]),
        ] as Rect;
      }

      return box;
    }
    return b;
  };

export type Crop = Rect;

const DEFAULT_STROKE_WIDTH = 2;

type ImageBoxOptions = {
  boxRect?: SVGProps<SVGRectElement>;
  badgeRect?: SVGProps<SVGRectElement>;
  imageRect?: {
    width?: number;
    height?: number;
    backgroundColor?: string;
    borderRadius?: number;
    padding?: number;
  };
};

export type ImageBox = {
  shape: Rect;
  item?: ProductRecognitionBBox | ProductRecognitionPolygon;
  label?: string;
  badge?: string;
  color: string;
  strokeWidth?: number;
  opacity?: number;
  image?: string;
  options?: ImageBoxOptions;
  // Polygon support
  isPolygon?: boolean;
  points?: Array<{ x: number; y: number }>;
  shelfColumnLabel?: string;
  embeddingCropCount?: number | null;
  // PNG mask overlay (base64-encoded PNG with transparency)
  maskImage?: string;
};

type BoxedImageProps = Partial<HTMLDivElement> & {
  image: HTMLImageElement;
  crop?: Crop;
  cropPadding?: number;
  boxes: ImageBox[];
  focusModeEnabled?: boolean;
  defaultFillOpacity?: number;
  onBoxClick?: (index: number) => void;
  onBoxRightClick?: (index: number) => void;
  focusedBoxId?: string;
  styles?: React.CSSProperties | undefined;
  onBoxesSelected?: (selectedBoxIndices: number[]) => void;
  onNewShapeCreated?: (rect: Rect) => void;
  showEmbeddingsCount?: boolean;
};

export const BoxedImage: FC<BoxedImageProps> = ({
  image,
  crop,
  cropPadding = 0,
  boxes,
  focusModeEnabled,
  defaultFillOpacity = 0,
  onBoxClick,
  onBoxRightClick,
  focusedBoxId,
  styles,
  onBoxesSelected,
  onNewShapeCreated,
  showEmbeddingsCount = false,
  ...props
}) => {
  // Process 1-bit mask PNGs into semi-transparent colored overlays
  const [processedMasks, setProcessedMasks] = useState<Record<number, string>>({});
  useEffect(() => {
    const masksToProcess = boxes
      .map((box, index) => (box.maskImage ? { index, b64: box.maskImage, color: box.color } : null))
      .filter(Boolean) as { index: number; b64: string; color: string }[];

    if (masksToProcess.length === 0) {
      setProcessedMasks({});
      return;
    }

    Promise.all(
      masksToProcess.map(({ index, b64, color }) =>
        processMaskImage(b64, image.naturalWidth, image.naturalHeight, color).then((dataUrl) => ({
          index,
          dataUrl,
        }))
      )
    ).then((results) => {
      const maskMap: Record<number, string> = {};
      results.forEach(({ index, dataUrl }) => {
        maskMap[index] = dataUrl;
      });
      setProcessedMasks(maskMap);
    });
  }, [boxes, image]);

  const hash = calculateHash(
    image.src + crop?.toString() + boxes.map((b) => b.shape.toString()).join(',')
  );
  const adjustedCrop = useMemo(() => {
    const imageCrop = crop || [0, 0, image.naturalWidth, image.naturalHeight];
    if (boxes.length > 0) {
      const xs = boxes.flatMap((b) => [b.shape[0], b.shape[0] + b.shape[2]]);
      const ys = boxes.flatMap((b) => [b.shape[1], b.shape[1] + b.shape[3]]);

      let x = Math.min(...xs);
      let y = Math.min(...ys);
      let width = Math.max(...xs) - x;
      let height = Math.max(...ys) - y;

      const padding = Math.max(
        (image.naturalWidth / 3 - width) / 2,
        (image.naturalHeight / 3 - height) / 2
      );
      x = x - padding;
      y = y - padding;
      width = width + padding * 2;
      height = height + padding * 2;

      const cropRatio = imageCrop[3] / imageCrop[2];
      // Adjust x, y, width and height to match the crop ratio
      if (height / width > cropRatio) {
        const newWidth = height / cropRatio;
        x = x - (newWidth - width) / 2;
        width = newWidth;
      } else {
        const newHeight = width * cropRatio;
        y = y - (newHeight - height) / 2;
        height = newHeight;
      }

      if (x < 0) {
        x = 0;
      }
      if (x + width > image.naturalWidth) {
        x = image.naturalWidth - width;
      }

      if (y < 0) {
        y = 0;
      }
      if (y + height > image.naturalHeight) {
        y = image.naturalHeight - height;
      }

      return [x, y, width, height] as Crop;
    }

    return imageCrop;
  }, [crop, boxes, image]);

  const paddedCrop = crop
    ? bboxPadding(cropPadding, [image.naturalWidth, image.naturalHeight])(crop)
    : null;

  const viewBox =
    Array.isArray(paddedCrop) && paddedCrop.length === 4
      ? paddedCrop.join(' ')
      : `0 0 ${image.naturalWidth} ${image.naturalHeight}`;

  const adjustedViewBox =
    boxes.length > 0
      ? bboxPadding(cropPadding, [image.naturalWidth, image.naturalHeight])(adjustedCrop).join(' ')
      : viewBox;

  const isTouch = 'ontouchstart' in document.documentElement;

  const easeInRef = React.useRef<SVGAnimateElement | null>(null);
  const easeOutRef = React.useRef<SVGAnimateElement | null>(null);

  const animationRef = React.useRef<NodeJS.Timeout | null>(null);
  const focusPoint = React.useRef({ x: 0, y: 0 });
  const [focusModeActive, setFocusModeActive] = React.useState(false);

  // Rectangle selection state
  const [selectionStart, setSelectionStart] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [selectionEnd, setSelectionEnd] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const imgRef = React.useRef<SVGImageElement>(null);

  const handleFocusOn = (e: any) => {
    e.stopPropagation();
    if (e.type !== 'touchstart') {
      e.preventDefault();
    }
    const { pageX: pointerX, pageY: pointerY } = e.targetTouches?.[0] ?? {
      pageX: 0,
      pageY: 0,
    };
    focusPoint.current = { x: e.clientX ?? pointerX, y: e.clientY ?? pointerY };
    animationRef.current = setTimeout(() => {
      // @ts-ignore - TS doesn't know about beginElement
      easeInRef.current?.beginElement();
      setFocusModeActive(true);
    }, 200);
    // @ts-ignore - TS doesn't know about beginElement
    (isTouch ? props.ontouchend : props.onmousedown)?.(e);
  };
  const handleFocusOff = (e: any) => {
    e.stopPropagation();
    if (animationRef.current) {
      clearTimeout(animationRef.current);
    }
    if (!focusModeActive) {
      return;
    }
    // @ts-ignore - TS doesn't know about beginElement
    easeOutRef.current?.beginElement();
    setFocusModeActive(false);
    // @ts-ignore - TS doesn't know about beginElement
    (isTouch ? props.ontouchend : props.onmousedown)?.(e);
  };
  const handleFocusCancel = (e: any) => {
    e.stopPropagation();

    const { pageX: pointerX, pageY: pointerY } = e.targetTouches[0];

    if (
      (Math.abs(pointerX - focusPoint.current.x) > 10 ||
        Math.abs(pointerY - focusPoint.current.y) > 10) &&
      animationRef.current
    ) {
      clearTimeout(animationRef.current);
    }
    // @ts-ignore - TS doesn't know about beginElement
    props.ontouchmove?.(e);
  };

  // Helper function to convert mouse coordinates to SVG coordinates
  const getSVGCoordinates = (e: MouseEvent): { x: number; y: number } => {
    return getContainedSVGCoordinates(
      e,
      imgRef.current,
      focusModeActive ? adjustedViewBox : viewBox
    );
  };

  // Rectangle selection handlers
  const handleSelectionStart = (e: MouseEvent) => {
    if (!e.shiftKey) return;

    e.preventDefault();
    e.stopPropagation();

    const coords = getSVGCoordinates(e);
    setSelectionStart(coords);
    setSelectionEnd(coords);
  };

  const handleSelectionMove = (e: MouseEvent) => {
    if (!selectionStart) return;

    e.preventDefault();
    e.stopPropagation();

    const coords = getSVGCoordinates(e);
    setSelectionEnd(coords);
  };

  // Find all boxes that overlap with the selection rectangle
  const findOverlappingBoxes = (selectionRect: Rect): number[] => {
    const overlapping: number[] = [];

    boxes.forEach((box, index) => {
      // Check rectangle overlap
      if (areBoxOverlapping(box.shape, selectionRect, 0.1)) {
        overlapping.push(index);
      }
    });

    return overlapping;
  };

  const handleSelectionEnd = (e: MouseEvent) => {
    if (!selectionStart || !selectionEnd || (!onNewShapeCreated && !onBoxesSelected)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Calculate selection rectangle
    const selectionRect: Rect = [
      Math.min(selectionStart.x, selectionEnd.x),
      Math.min(selectionStart.y, selectionEnd.y),
      Math.abs(selectionEnd.x - selectionStart.x),
      Math.abs(selectionEnd.y - selectionStart.y),
    ];

    if (onNewShapeCreated) {
      onNewShapeCreated(selectionRect.map((value) => Math.round(value)) as Rect);
    } else if (onBoxesSelected) {
      // Find overlapping boxes
      const selectedIndices = findOverlappingBoxes(selectionRect);

      // Call the callback with selected box indices
      onBoxesSelected(selectedIndices);
    }

    // Reset selection state
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  const boxStyle = onBoxClick ? { cursor: 'pointer' } : {};
  const canDraw = !isTouch && (onNewShapeCreated || (onBoxesSelected && boxes.length > 0));

  return (
    <svg
      className="w-full"
      viewBox={viewBox}
      style={styles}
      {...((focusModeEnabled && boxes.length > 0
        ? {
            onmousedown: !isTouch ? handleFocusOn : props.onmousedown,
            onmouseup: !isTouch ? handleFocusOff : props.onmouseup,
            onmouseleave: !isTouch ? handleFocusOff : props.onmouseleave,
            ontouchstart: isTouch ? handleFocusOn : props.ontouchstart,
            ontouchend: isTouch ? handleFocusOff : props.ontouchend,
            ontouchmove: isTouch ? handleFocusCancel : undefined,
          }
        : {}) as unknown as Partial<HTMLOrSVGElement>)}
      onContextMenu={(e: any) => e.preventDefault()}
      {...(canDraw
        ? {
            onMouseDown: (e) => {
              handleSelectionStart(e.nativeEvent);
            },
            onMouseMove: (e) => {
              handleSelectionMove(e.nativeEvent);
            },
            onMouseUp: (e) => {
              handleSelectionEnd(e.nativeEvent);
            },
          }
        : {})}
    >
      <image
        ref={imgRef}
        href={image.src}
        width={image.naturalWidth}
        height={image.naturalHeight}
      />
      {/* Render processed PNG mask overlays (e.g. visual obstructions) */}
      {Object.entries(processedMasks).map(([index, dataUrl]) => (
        <image
          key={`mask-${index}`}
          href={dataUrl}
          x={0}
          y={0}
          width={image.naturalWidth}
          height={image.naturalHeight}
          style={{ pointerEvents: 'none' }}
        />
      ))}
      {boxes.map((box, index) => (
        <g key={index} className="box-group">
          {box.label && (
            <text x={box.shape[0]} y={box.shape[1] - 5} fontSize={12}>
              {box.label}
            </text>
          )}
          {box.isPolygon && box.points ? (
            // Render polygon
            <polygon
              points={box.points.map((p) => `${p.x},${p.y}`).join(' ')}
              fill={box.color}
              fillOpacity={0}
              stroke={box.color}
              strokeWidth={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
              style={boxStyle}
              {...(onBoxClick && { onClick: () => onBoxClick(index) })}
              {...(onBoxRightClick && {
                onContextMenu: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onBoxRightClick(index);
                },
              })}
            />
          ) : (
            // Render rectangle
            <rect
              x={box.shape[0]}
              y={box.shape[1]}
              width={box.shape[2]}
              height={box.shape[3]}
              fill={box.color}
              fillOpacity={0}
              stroke={box.color}
              strokeWidth={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
              rx={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
              style={boxStyle}
              {...box.options?.boxRect}
              {...(onBoxClick && { onClick: () => onBoxClick(index) })}
              {...(onBoxRightClick && {
                onContextMenu: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onBoxRightClick(index);
                },
              })}
            />
          )}
          {box.image && <BoxImage box={box} />}
          {box.badge && <BoxBadge box={box} />}
          {/* Render embedding count badge inside the box group for hover behavior */}
          {showEmbeddingsCount &&
            box.embeddingCropCount !== undefined &&
            box.embeddingCropCount !== null && (
              <g className="embedding-count-badge" style={{ pointerEvents: 'none' }}>
                {(() => {
                  const boxWidth = box.shape[2];
                  const boxHeight = box.shape[3];
                  const minDimension = Math.min(boxWidth, boxHeight);
                  const scaleFactor = Math.min(1, minDimension / 100);
                  const badgeWidth = 56 * scaleFactor;
                  const badgeHeight = 44 * scaleFactor;
                  const fontSize = 30 * scaleFactor;
                  const strokeWidth = 2 * scaleFactor;
                  const borderRadius = 6 * scaleFactor;
                  const margin = 4 * scaleFactor;
                  const bgColor =
                    box.embeddingCropCount < 5
                      ? 'rgba(249, 115, 22, 0.9)'
                      : 'rgba(34, 197, 94, 0.9)';
                  const badgeX = box.shape[0] + boxWidth - badgeWidth - margin;
                  const badgeY = box.shape[1] + boxHeight - badgeHeight - margin;

                  return (
                    <>
                      <rect
                        x={badgeX}
                        y={badgeY}
                        width={badgeWidth}
                        height={badgeHeight}
                        fill={bgColor}
                        stroke="#fff"
                        strokeWidth={strokeWidth}
                        rx={borderRadius}
                      />
                      <text
                        x={badgeX + badgeWidth / 2}
                        y={badgeY + badgeHeight / 2}
                        fontSize={fontSize}
                        fontWeight="bold"
                        fill="#fff"
                        dominantBaseline="central"
                        textAnchor="middle"
                      >
                        {box.embeddingCropCount}
                      </text>
                    </>
                  );
                })()}
              </g>
            )}
        </g>
      ))}

      {/* Render location labels on top of everything */}
      {boxes.map((box, index) => {
        if (!box.shelfColumnLabel) return null;

        // Calculate adaptive label size based on box dimensions
        const boxWidth = box.shape[2];
        const boxHeight = box.shape[3];
        const minDimension = Math.min(boxWidth, boxHeight);

        // Scale label dimensions based on box size
        // For boxes smaller than 80px, scale down proportionally
        const scaleFactor = Math.min(1, minDimension / 120);
        const labelWidth = 120 * scaleFactor;
        const labelHeight = 60 * scaleFactor;
        const fontSize = 36 * scaleFactor;
        const strokeWidth = 2 * scaleFactor;
        const borderRadius = 6 * scaleFactor;

        return (
          <React.Fragment key={`label-${index}`}>
            <g style={{ pointerEvents: 'none' }}>
              <rect
                x={box.shape[0] + box.shape[2] / 2 - labelWidth / 2}
                y={box.shape[1] + box.shape[3] / 2 - labelHeight / 2}
                width={labelWidth}
                height={labelHeight}
                fill="rgba(0, 0, 0, 0.85)"
                stroke="#fff"
                strokeWidth={strokeWidth}
                rx={borderRadius}
              />
              <text
                x={box.shape[0] + box.shape[2] / 2}
                y={box.shape[1] + box.shape[3] / 2}
                fontSize={fontSize}
                fontWeight="bold"
                fill="#fff"
                dominantBaseline="central"
                textAnchor="middle"
              >
                {box.shelfColumnLabel}
              </text>
            </g>
          </React.Fragment>
        );
      })}

      {/* CSS to hide embedding count badge when hovering over parent box */}
      <style>{`.box-group:hover .embedding-count-badge { opacity: 0; }`}</style>

      {/*draw box with a chosen id once again to put it on top*/}
      {focusedBoxId && <use xlinkHref={`#${focusedBoxId}`} />}

      {/* Render selection rectangle during drag */}
      {selectionStart && selectionEnd && (
        <rect
          x={Math.min(selectionStart.x, selectionEnd.x)}
          y={Math.min(selectionStart.y, selectionEnd.y)}
          width={Math.abs(selectionEnd.x - selectionStart.x)}
          height={Math.abs(selectionEnd.y - selectionStart.y)}
          fill="rgba(33,150,243, 0.6)"
          stroke="rgba(33,150,243, 0.9)"
          strokeWidth="2"
          strokeDasharray="5,5"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {boxes.length > 0 && (
        <>
          <mask id={hash} style={{ pointerEvents: 'none' }}>
            <rect
              x={0}
              y={0}
              width={image.naturalWidth}
              height={image.naturalHeight}
              fill="white"
            />

            {boxes.map((box, index) =>
              box.isPolygon && box.points ? (
                <polygon
                  key={index}
                  points={box.points.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="black"
                  stroke="black"
                  strokeWidth={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
                />
              ) : (
                <rect
                  key={index}
                  x={box.shape[0]}
                  y={box.shape[1]}
                  width={box.shape[2]}
                  height={box.shape[3]}
                  rx={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
                  fill="black"
                  stroke="black"
                  strokeWidth={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
                  {...box.options?.boxRect}
                />
              )
            )}
          </mask>
          <rect
            x={0}
            y={0}
            width={image.naturalWidth}
            height={image.naturalHeight}
            fill="#ffffff"
            fillOpacity={0}
            style={{ transition: 'fill-opacity 0.5s', pointerEvents: 'none' }}
            mask={`url(#${hash})`}
            id={`rect-${hash}`}
          />
          {focusModeEnabled && (
            <>
              <animate
                ref={easeInRef}
                attributeName="viewBox"
                to={adjustedViewBox}
                dur="0.5s"
                repeatCount="1"
                fill="freeze"
              />
              <animate
                ref={easeOutRef}
                attributeName="viewBox"
                to={viewBox}
                dur="0.5s"
                repeatCount="1"
                fill="freeze"
              />
            </>
          )}
        </>
      )}
    </svg>
  );
};

const calculateHash = (str: string): string => {
  let hash = 0;
  if (str.length === 0) {
    return hash.toString();
  }
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    // tslint:disable-next-line:no-bitwise
    hash = (hash << 5) - hash + char;
    // tslint:disable-next-line:no-bitwise
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString();
};

type BoxBadgeProps = {
  box: ImageBox;
  options?: ImageBoxOptions;
};

const badgeSize = 36;

const BoxBadge: FC<BoxBadgeProps> = ({ box: { shape, badge, options } }) => {
  const scaledBadgeSize = badgeSize;
  const scaledBadgeMargin = scaledBadgeSize / 3;
  const scaledBadgeRadius = scaledBadgeSize / 6;
  const scaledBadgeFontSize = (scaledBadgeSize * 3) / 4;

  return (
    <>
      <rect
        style={{ pointerEvents: 'none' }}
        x={shape[0] + scaledBadgeMargin}
        y={shape[1] + scaledBadgeMargin}
        width={scaledBadgeSize}
        height={scaledBadgeSize}
        fill="rgba(4,135,124,0.8)"
        rx={scaledBadgeRadius}
        ry={scaledBadgeRadius}
        strokeLinejoin="round"
        {...options?.badgeRect}
      />
      <text
        style={{ pointerEvents: 'none' }}
        x={
          shape[0] +
          scaledBadgeMargin +
          (options?.badgeRect?.width
            ? parseInt(`${options?.badgeRect.width}`, 10)
            : scaledBadgeSize) /
            2
        }
        y={
          shape[1] +
          scaledBadgeMargin +
          (options?.badgeRect?.height
            ? parseInt(`${options?.badgeRect.height}`, 10)
            : scaledBadgeSize) /
            2
        }
        fontSize={scaledBadgeFontSize}
        fill="#fff"
        dominantBaseline="central"
        textAnchor="middle"
      >
        {badge}
      </text>
    </>
  );
};

type BoxImageProps = {
  box: ImageBox;
};

const BoxImage: FC<BoxImageProps> = ({
  box: {
    shape,
    image,
    isPolygon,
    points,
    options: { imageRect } = {
      imageRect: {},
    },
  },
}) => {
  if (!image) {
    return null;
  }

  // Calculate box dimensions based on shape or points
  let boxWidth: number;
  let boxHeight: number;
  let centerX: number;
  let centerY: number;

  if (isPolygon && points && points.length > 0) {
    // For polygon boxes, calculate bounding box from points
    const xCoords = points.map((p) => p.x);
    const yCoords = points.map((p) => p.y);
    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    boxWidth = maxX - minX;
    boxHeight = maxY - minY;
    centerX = minX + boxWidth / 2;
    centerY = minY + boxHeight / 2;
  } else {
    // For rectangular boxes, use shape [x, y, width, height]
    boxWidth = shape[2];
    boxHeight = shape[3];
    centerX = shape[0] + boxWidth / 2;
    centerY = shape[1] + boxHeight / 2;
  }

  // Calculate image dimensions as percentage of box size (max 70% of box dimensions)
  const maxImageWidth = Math.max(boxWidth, imageRect?.width ?? 150);
  const maxImageHeight = Math.max(boxHeight, imageRect?.height ?? 150);

  // Ensure minimum size but not larger than box
  const width = Math.max(30, Math.min(maxImageWidth, boxWidth - 20));
  const height = Math.max(30, Math.min(maxImageHeight, boxHeight - 20));

  const backgroundColor = imageRect?.backgroundColor ?? '#ffffff';
  const borderRadius = imageRect?.borderRadius ?? 8;
  const padding = imageRect?.padding ?? 4;

  const x = centerX - width / 2;
  const y = centerY - height / 2;

  return (
    <>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={borderRadius}
        fill={backgroundColor}
        style={{ pointerEvents: 'none' }}
      />
      <image
        x={x + padding}
        y={y + padding}
        href={image}
        height={height - padding * 2}
        width={width - padding * 2}
        style={{ pointerEvents: 'none' }}
      />
    </>
  );
};
